import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Button,
  Table,
  Form,
  Badge,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import ArticuloService from "../../services/articulo.service";
import UserService from "../../services/user.service";
import { calcularPrecioVenta } from "../../utils/precios";
import EventBus from "../../common/EventBus";
import SearchIcon from "@mui/icons-material/Search";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Lottie from "react-lottie";
import * as lapiz from "../../assets/reloj.json";

const ITEMS_PER_PAGE = 25;

const MisPrecios = () => {
  const user = JSON.parse(localStorage.getItem("user"));
  const gananciaGlobal = parseFloat(user?.ganancia || 0);

  const [arts, setArts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [totalResults, setTotalResults] = useState(0);

  // Mapa de inputs editables: { [articulos_id]: string }
  const [editingValues, setEditingValues] = useState({});

  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  const lapízOptions = {
    loop: true,
    autoplay: true,
    animationData: lapiz.default,
    rendererSettings: {
      preserveAspectRatio: "xMidYMid slice",
    },
  };

  const loadPage = useCallback(
    async (pageNum, searchTerm = "", append = false) => {
      try {
        const response = await ArticuloService.getPaginado(
          pageNum,
          ITEMS_PER_PAGE,
          searchTerm
        );
        const { content, totalElements, totalPages, number } = response.data;

        if (append) {
          setArts((prev) => [...prev, ...content]);
        } else {
          setArts(content);
        }
        setTotalResults(totalElements);
        setPage(number + 1);
        setHasMore(number + 1 < totalPages);
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 401) {
          EventBus.dispatch("logout");
        } else {
          toast.error("Error al cargar artículos");
        }
        setHasMore(false);
      }
    },
    []
  );

  // Carga inicial
  useEffect(() => {
    setLoading(true);
    loadPage(0, "").finally(() => setLoading(false));
  }, [loadPage]);

  // Debounce de búsqueda
  const handleSearchChange = (value) => {
    setSearch(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setSearching(true);
      loadPage(0, value).finally(() => {
        setSearching(false);
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      });
    }, 400);
  };

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleLoadMore = () => {
    if (!hasMore || loading || searching) return;
    loadPage(page, search, true);
  };

  const handleInputChange = (articulos_id, value) => {
    // Solo permitir números y punto decimal
    let str = value.replace(/[^0-9.]+/g, "");
    if (str.includes(".")) {
      const parts = str.split(".");
      if (parts.length > 2) {
        str = parts[0] + "." + parts.slice(1).join("");
      }
    }
    setEditingValues((prev) => ({ ...prev, [articulos_id]: str }));
  };

  const handleGuardar = async (art) => {
    const rawValue = editingValues[art.articulos_id];
    if (rawValue === undefined || rawValue === "") {
      toast.warning("Ingresá un porcentaje de ganancia");
      return;
    }
    const ganancia = parseFloat(rawValue);
    if (isNaN(ganancia) || ganancia < 0 || ganancia > 999.99) {
      toast.error("La ganancia debe ser un número entre 0 y 999.99");
      return;
    }

    try {
      await UserService.crearOActualizarGanancia(art.articulos_id, ganancia);
      toast.success(`Ganancia personalizada guardada: ${ganancia}%`);
      // Actualizar localmente
      setArts((prev) =>
        prev.map((a) =>
          a.articulos_id === art.articulos_id
            ? { ...a, ganancia_personalizada: ganancia }
            : a
        )
      );
    } catch (error) {
      console.error(error);
      toast.error("Error al guardar la ganancia personalizada");
    }
  };

  const handleUsarGlobal = async (art) => {
    try {
      await UserService.eliminarGananciaPersonalizada(art.articulos_id);
      toast.success("Ganancia personalizada eliminada. Ahora usa el % global.");
      // Actualizar localmente
      setArts((prev) =>
        prev.map((a) =>
          a.articulos_id === art.articulos_id
            ? { ...a, ganancia_personalizada: null }
            : a
        )
      );
      // Limpiar input
      setEditingValues((prev) => {
        const copy = { ...prev };
        delete copy[art.articulos_id];
        return copy;
      });
    } catch (error) {
      console.error(error);
      toast.error("Error al eliminar la ganancia personalizada");
    }
  };

  const isPersonalizada = (art) =>
    art.ganancia_personalizada !== null &&
    art.ganancia_personalizada !== undefined;

  return (
    <Container fluid className="tienda-container">
      <h2 className="mb-4">Mis precios personalizados</h2>

      {/* Barra de búsqueda */}
      <div className="tienda-search-bar mb-3">
        <div className="tienda-search-wrapper">
          <SearchIcon className="tienda-search-icon" />
          <input
            ref={searchInputRef}
            type="text"
            className="tienda-search-input"
            placeholder="Buscar artículos..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {search && (
            <button
              className="tienda-search-clear"
              onClick={() => handleSearchChange("")}
              title="Limpiar"
            >
              <ClearAllIcon />
            </button>
          )}
        </div>
      </div>

      {search && (
        <div className="tienda-results-count mb-3">
          {totalResults} resultado{totalResults !== 1 ? "s" : ""} para "{search}"
        </div>
      )}

      {loading && arts.length === 0 ? (
        <div className="tienda-loading-global">
          <Lottie options={lapízOptions} height={200} width={200} />
          <p className="tienda-loading-text">Cargando artículos...</p>
        </div>
      ) : (
        <>
          <div className="table-responsive">
            <Table striped hover className="align-middle bg-white">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: 120 }}>Código</th>
                  <th style={{ minWidth: 250 }}>Descripción</th>
                  <th className="text-end" style={{ minWidth: 120 }}>
                    Precio base
                  </th>
                  <th className="text-center" style={{ minWidth: 140 }}>
                    % Actual
                  </th>
                  <th style={{ minWidth: 200 }}>% Nuevo</th>
                  <th className="text-end" style={{ minWidth: 130 }}>
                    Precio calculado
                  </th>
                  <th style={{ minWidth: 160 }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {arts.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-5 text-muted">
                      No se encontraron artículos
                    </td>
                  </tr>
                ) : (
                  arts.map((art) => {
                    const precioCalculado = calcularPrecioVenta(
                      art,
                      gananciaGlobal
                    );
                    const inputValue =
                      editingValues[art.articulos_id] !== undefined
                        ? editingValues[art.articulos_id]
                        : isPersonalizada(art)
                        ? String(art.ganancia_personalizada)
                        : "";

                    return (
                      <tr key={art.articulos_id}>
                        <td className="fw-semibold">{art.codigo}</td>
                        <td>{art.descri}</td>
                        <td className="text-end">
                          ${parseFloat(art.PRE_VTA || 0).toFixed(2)}
                        </td>
                        <td className="text-center">
                          {isPersonalizada(art) ? (
                            <Badge bg="success" text="white">
                              {parseFloat(
                                art.ganancia_personalizada
                              ).toFixed(2)}
                              %
                            </Badge>
                          ) : (
                            <Badge bg="secondary" text="white">
                              {gananciaGlobal.toFixed(2)}% (global)
                            </Badge>
                          )}
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <Form.Control
                              type="text"
                              size="sm"
                              style={{ maxWidth: 90 }}
                              placeholder="%"
                              value={inputValue}
                              onChange={(e) =>
                                handleInputChange(
                                  art.articulos_id,
                                  e.target.value
                                )
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  e.preventDefault();
                                  handleGuardar(art);
                                }
                              }}
                            />
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleGuardar(art)}
                              disabled={
                                editingValues[art.articulos_id] === undefined
                              }
                            >
                              <SaveIcon fontSize="small" />
                            </Button>
                          </div>
                        </td>
                        <td className="text-end fw-bold text-success">
                          ${precioCalculado.toFixed(2)}
                        </td>
                        <td>
                          {isPersonalizada(art) && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleUsarGlobal(art)}
                            >
                              <DeleteOutlineIcon fontSize="small" /> Usar
                              global
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>

          {hasMore && (
            <div className="text-center py-4">
              <Button
                variant="light"
                className="tienda-action-btn"
                onClick={handleLoadMore}
                disabled={searching}
              >
                {searching ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Cargando...
                  </>
                ) : (
                  "Cargar más artículos"
                )}
              </Button>
            </div>
          )}

          {!hasMore && arts.length > 0 && (
            <div className="tienda-no-more">No hay más artículos</div>
          )}
        </>
      )}
    </Container>
  );
};

export default MisPrecios;
